/**
 * App.js - Application Bootstrap and Route Configuration
 * 
 * This file is responsible for:
 * - App bootstrap (providers, global components)
 * - Route configuration
 * - Protected/public route wiring
 * 
 * Navigation and layout logic lives in /components/layout/Layout.js
 */
import { lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";

// Auth & Tenant Providers
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { TenantProvider } from "./contexts/TenantContext";

// Protected Route from common components
import { ProtectedRoute } from "./components/common";

// Layout components (single source of truth for navigation)
import { ResponsiveLayout } from "./components/layout";

// API utilities
import { API } from "./utils/api";

// ============================================
// CORE PAGES - Loaded immediately (most used)
// ============================================
import { HomePage } from "./pages/Home";
import { LoginPage } from "./pages/Login";
import { ProfilePage } from "./pages/Profile";
import { CompanyProfilePage } from "./pages/Company";

// ============================================
// LAZY LOADED PAGES - Loaded on demand
// ============================================
const CalendarPage = lazy(() => import("./pages/Calendar").then(m => ({ default: m.CalendarPage })));
const AdminPanelPage = lazy(() => import("./pages/Admin").then(m => ({ default: m.AdminPanelPage })));
const CreateCompanyPage = lazy(() => import("./pages/Company").then(m => ({ default: m.CreateCompanyPage })));
const TeamLeaderDashboardPage = lazy(() => import("./pages/TeamLeader").then(m => ({ default: m.TeamLeaderDashboardPage })));
const ManagerDashboardPage = lazy(() => import("./pages/Dashboard").then(m => ({ default: m.ManagerDashboardPage })));
const AgentDetailPage = lazy(() => import("./pages/Dashboard").then(m => ({ default: m.AgentDetailPage })));
const BadgesPage = lazy(() => import("./pages/Badges").then(m => ({ default: m.BadgesPage })));
const LeaderboardPage = lazy(() => import("./pages/Leaderboard").then(m => ({ default: m.LeaderboardPage })));
const AddActivityPage = lazy(() => import("./pages/Activity").then(m => ({ default: m.AddActivityPage })));
const ManagerPanelPage = lazy(() => import("./pages/Manager").then(m => ({ default: m.ManagerPanelPage })));
const CompanyDiscoveryPage = lazy(() => import("./pages/Discovery").then(m => ({ default: m.CompanyDiscoveryPage })));
const SuperAdminPage = lazy(() => import("./pages/SuperAdmin").then(m => ({ default: m.SuperAdminPage })));
const TargetListsPage = lazy(() => import("./pages/TargetLists").then(m => ({ default: m.TargetListsPage })));
const TargetListDetailPage = lazy(() => import("./pages/TargetLists").then(m => ({ default: m.TargetListDetailPage })));
const PlaybookPage = lazy(() => import("./pages/Playbook").then(m => ({ default: m.PlaybookPage })));

// ============================================
// LOADING COMPONENT
// ============================================
const PageLoader = () => (
  <div className="mobile-container flex items-center justify-center min-h-screen bg-gray-50">
    <div className="flex flex-col items-center gap-3">
      <div className="w-8 h-8 border-3 border-[#1B3F8F] border-t-transparent rounded-full animate-spin"></div>
      <span className="text-sm text-gray-500">Učitavanje...</span>
    </div>
  </div>
);

// ============================================
// PAGE WRAPPERS (Connect pages with layout)
// ============================================

/**
 * Calendar Page Wrapper
 * Wraps CalendarPage with ResponsiveLayout and passes auth context
 */
const CalendarPageWrapper = () => {
  const { user, token } = useAuth();
  return (
    <ResponsiveLayout title="Kalendar">
      <CalendarPage user={user} token={token} apiUrl={API} />
    </ResponsiveLayout>
  );
};

// ============================================
// ROUTE CONFIGURATION
// ============================================
const routeConfig = [
  // Core routes (loaded immediately)
  { path: "/login", element: <LoginPage />, protected: false },
  { path: "/", element: <HomePage />, protected: true },
  { path: "/profile", element: <ProfilePage />, protected: true },
  { path: "/company/:companyId", element: <CompanyProfilePage />, protected: true },
  
  // Lazy loaded routes
  { path: "/calendar", element: <CalendarPageWrapper />, protected: true },
  { path: "/leaderboard", element: <LeaderboardPage />, protected: true },
  { path: "/badges", element: <BadgesPage />, protected: true },
  { path: "/company/:companyId/activity/new", element: <AddActivityPage />, protected: true },
  { path: "/dashboard", element: <ManagerDashboardPage />, protected: true },
  { path: "/team-leader-dashboard", element: <TeamLeaderDashboardPage />, protected: true },
  { path: "/dashboard/agent/:userId", element: <AgentDetailPage />, protected: true },
  { path: "/admin", element: <AdminPanelPage />, protected: true },
  { path: "/manager-panel", element: <ManagerPanelPage />, protected: true },
  { path: "/company/new", element: <CreateCompanyPage />, protected: true },
  { path: "/discovery", element: <CompanyDiscoveryPage />, protected: true },
  { path: "/super-admin", element: <SuperAdminPage />, protected: true },
  { path: "/target-lists", element: <TargetListsPage />, protected: true },
  { path: "/target-lists/:id", element: <TargetListDetailPage />, protected: true },
  { path: "/playbook", element: <PlaybookPage />, protected: true },
];

// ============================================
// MAIN APP COMPONENT
// ============================================
function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <TenantProvider>
        <Toaster position="top-center" richColors />
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {routeConfig.map(({ path, element, protected: isProtected }) => (
              <Route
                key={path}
                path={path}
                element={
                  isProtected ? (
                    <ProtectedRoute>{element}</ProtectedRoute>
                  ) : (
                    element
                  )
                }
              />
            ))}
            {/* Catch-all redirect */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
        </TenantProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
